import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import portrait from "@/assets/tamal-portrait-photo.webp";

export const Route = createFileRoute("/press")({
  head: () =>
    buildSeo({
      title: "Press & Media Kit — Rahatul Ashiq Tamal, SEO Consultant",
      description:
        "Bio, headshot, quick facts and contact details for journalists, podcasters and list writers covering SEO, AEO and GEO in Bangladesh.",
      path: "/press",
      ogEyebrow: "Press",
    }),
  component: PressPage,
});

const SHORT_BIO = `${SITE.fullName} (Freelancer Tamal) is an SEO, AEO and GEO consultant based in ${SITE.city}, ${SITE.country}. His service areas include technical SEO, local search, content strategy and AI-search readiness.`;

const FACTS = [
  ["Name", SITE.fullName],
  ["Brand", SITE.name],
  ["Based in", `${SITE.city}, ${SITE.country}`],
  ["Focus", "SEO, answer engine optimization (AEO), generative engine optimization (GEO)"],
  ["Website", SITE.url],
];

const TOPICS = [
  "How small businesses can show up in ChatGPT and AI Overviews",
  "Local SEO for businesses in Bangladesh",
  "Hiring a freelance SEO consultant safely",
  "Technical SEO and structured data basics",
];

function PressPage() {
  return (
    <>
      <Section className="!pt-12 md:!pt-20">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Press", path: "/press" }]} />
        <h1 className="mt-4 font-display text-4xl md:text-5xl">Press &amp; media kit</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Writing about SEO or AI search? Everything you need to quote or feature me is on this page. You're welcome to use the bio and photo with a link back to {SITE.url.replace("https://", "")}.
        </p>
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <img src={portrait} alt={`Headshot of ${SITE.fullName}`} className="w-full rounded-2xl border border-border" loading="lazy" />
            <a href={portrait} download className="mt-3 inline-block text-sm underline">Download headshot</a>
          </div>
          <div className="md:col-span-2 space-y-8">
            <div>
              <h2 className="font-display text-2xl">Short bio</h2>
              <p className="mt-3 text-muted-foreground">{SHORT_BIO}</p>
            </div>
            <div>
              <h2 className="font-display text-2xl">Quick facts</h2>
              <dl className="mt-3 grid gap-2 sm:grid-cols-[10rem_1fr]">
                {FACTS.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="font-medium">{k}</dt>
                    <dd className="text-muted-foreground break-words">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h2 className="font-display text-2xl">Topics I can comment on</h2>
              <ul className="mt-3 list-disc pl-5 text-muted-foreground space-y-1">
                {TOPICS.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
            <div>
              <h2 className="font-display text-2xl">Media contact</h2>
              <p className="mt-3 text-muted-foreground">
                Email <a className="underline" href={`mailto:${SITE.email}`}>{SITE.email}</a> — I usually reply within one working day. See also <Link to="/case-studies" className="underline">case studies</Link> and <Link to="/about" className="underline">my story</Link>.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        url: `${SITE.url}/press`,
        mainEntity: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName },
      }} />
    </>
  );
}
